/**
 * src/api/webchatChannel.js
 */
import { getAuthHeaders } from './auth';
import { getBaseUrl as BASE } from './utils';
// FastAPI reports HTTPException as {detail: "..."} and validation errors as
// {detail: [{msg, loc}, ...]} -- neither is `message` or `error`, so every
// backend rejection used to surface as a bare "Request failed". That hid real
// causes like "already connected to another organization" behind a shrug.
//
// Only 4xx detail is shown. Those strings are copy a developer wrote FOR the
// merchant ("This Facebook page is already connected to another
// organization"). A 5xx detail is either FastAPI's own default or an
// interpolated exception carrying site URLs, DNS/TLS internals or driver
// output -- never something to put in front of a user. `loc` is dropped from
// validation errors for the same reason: it names internal request fields.
const GENERIC_ERROR = 'Something went wrong on our side. Please try again.';
const MAX_ERROR_LEN = 300;

function _errText(d, status) {
  if (status >= 500) return GENERIC_ERROR;
  const det = d?.detail;
  let msg = '';
  if (typeof det === 'string') msg = det;
  else if (Array.isArray(det)) msg = det.map(e => e?.msg || '').filter(Boolean).join('; ');
  else msg = d?.message || d?.error || '';
  msg = String(msg).trim();
  if (!msg) return GENERIC_ERROR;
  return msg.length > MAX_ERROR_LEN ? msg.slice(0, MAX_ERROR_LEN) + '…' : msg;
}

async function _parse(r) {
  const d = await r.json().catch(() => null);
  if (!r.ok) {
    // Keep the real payload reachable in devtools without showing the user.
    if (r.status >= 500) console.error('[api]', r.status, d);
    throw new Error(_errText(d, r.status));
  }
  if (d === null) throw new Error(GENERIC_ERROR);  // 2xx with a non-JSON body
  return d.status === 'success' && d.data ? d.data : (d.data ?? d);
}

export const WEBCHAT_API_URL = `${BASE()}/bot/webchat`;

export function generateWidgetId(domain) {
  let h = 0; const s = `aerochat-${domain || 'default'}`;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h).toString(16).padStart(32, '0').slice(0, 32);
}

export function copyWebchatEmbedCode(embedCode) {
  navigator.clipboard.writeText(embedCode || '').catch(console.error);
}

export async function fetchWebchatFromDb() {
  try {
    const data = await _parse(await fetch(`${BASE()}/bot/webchat/account`, { headers: { Accept: 'application/json', ...getAuthHeaders() } }));
    if (data?.widget_id) return { exists: true, row: { widget_id: data.widget_id, webpage: data.domain, embedded_instruction: data.embed_code, display_mode: data.display_mode || 'floating' } };
    return { exists: false };
  } catch { return { exists: false }; }
}

export async function saveWebchatDomain(domain) {
  try {
    const data = await _parse(await fetch(`${BASE()}/bot/webchat/save-domain`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ domain }) }));
    return { ok: true, embedCode: data.embed_code, widgetId: data.widget_id };
  } catch (err) { return { ok: false, message: err.message }; }
}

export async function upsertWebchatRow(widgetId, webpage) {
  return saveWebchatDomain(webpage || '');
}

export async function fetchInstallStatus() {
  try {
    const data = await _parse(await fetch(`${BASE()}/bot/webchat/install-status`, { headers: { Accept: 'application/json', ...getAuthHeaders() } }));
    return { installed: data.installed || false, domain: data.installed_domain || null, installedAt: data.installed_at || null };
  } catch { return { installed: false, domain: null, installedAt: null }; }
}
